import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  availableLocaleCodes,
  fallbackLocale,
  loadLocaleMessages,
  localeLabels,
  normalizeLocale,
  translations,
} from './config'
import { I18nContext } from './context'
import type { Locale, Translations } from './types'

const STORAGE_LOCALE_KEY = 'ADMIN_LOCALE'

const resolveKey = (source: Record<string, unknown>, key: string) => {
  // prefer flat key lookup (supports keys with dots), fallback to nested path
  if (key in source) return source[key]
  return key.split('.').reduce<unknown>((acc, part) => {
    if (acc && typeof acc === 'object' && part in acc) return (acc as Record<string, unknown>)[part]
    return undefined
  }, source)
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [language, setLanguageState] = useState<Locale>(() => {
    if (typeof window !== 'undefined') {
      const stored = normalizeLocale(localStorage.getItem(STORAGE_LOCALE_KEY))
      if (stored) return stored
      const browser = normalizeLocale(navigator.language)
      if (browser) return browser
    }
    return fallbackLocale
  })
  const [messagesByLocale, setMessagesByLocale] =
    useState<Partial<Record<Locale, Translations>>>(() => ({ ...translations }))

  useEffect(() => {
    if (typeof window === 'undefined') return
    localStorage.setItem(STORAGE_LOCALE_KEY, language)
    document.documentElement.lang = language
  }, [language])

  useEffect(() => {
    let active = true

    loadLocaleMessages(language)
      .then((messages) => {
        if (!active) return
        setMessagesByLocale((current) => {
          if (current[language] === messages) return current
          return { ...current, [language]: messages }
        })
      })
      .catch(() => {
        // Keep rendering with loaded fallback messages or keys if the locale chunk is unavailable.
      })

    return () => {
      active = false
    }
  }, [language])

  const t = useCallback(
    (key: string, params?: Record<string, string | number>) => {
      const activeMessages = messagesByLocale[language]
      const fallbackMessages = messagesByLocale[fallbackLocale]
      const raw =
        (activeMessages ? resolveKey(activeMessages, key) : undefined) ??
        (fallbackMessages ? resolveKey(fallbackMessages, key) : undefined) ??
        key
      if (typeof raw !== 'string') return key
      if (!params) return raw
      return raw.replace(/{{(\w+)}}/g, (match, paramKey) => {
        const value = params[paramKey]
        return value !== undefined ? String(value) : match
      })
    },
    [language, messagesByLocale],
  )

  const setLanguage = useCallback((locale: Locale) => {
    const normalized = normalizeLocale(locale)
    if (!normalized) return
    setLanguageState(normalized)
  }, [])

  const availableLocales = useMemo(() => {
    return availableLocaleCodes.map((code) => ({
      code,
      label: localeLabels[code] ?? code,
    }))
  }, [])

  const value = useMemo(
    () => ({ language, setLanguage, t, availableLocales }),
    [language, setLanguage, t, availableLocales],
  )

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
}
